import type { RoomEvent, RoomMeta } from "../types";

/**
 * 모드 이벤트 상태(밀어내기 · 협동) — 연결(RoomConnection)이 **받는 즉시** 접어 둔다(타이머 · 스크립트처럼).
 * 모드 전환 FullState 에는 아직 짝·팀 결과가 없고(짝은 다음 틱에 `match` 이벤트로 온다 — room-sim SumoCtl.step),
 * 화면 조각(SumoPanel · useDriving · CarryPanel)은 새 모드가 그려진 뒤에야 구독한다. 그 사이에 온 이벤트를
 * 잃지 않도록 화면은 마운트할 때 이 상태에서 시작한다. 새 FullState(입장 · 재접속 · 모드 전환)마다 비운다
 * — FullState 의 meta.mode 가 그 시점의 정본이다.
 */
export type SumoState =
  | { phase: "wait"; next: boolean }
  | { phase: "match"; vs: string; endsTick: number }
  /** 상대가 3초 넘게 끊겨 멈춤 — untilTick 까지 기다린다(못 오면 무효 · 2026-09-29) */
  | { phase: "paused"; vs: string; untilTick: number }
  /** 모두 돌아와 3·2·1 — goTick 에 다시 시작(입력 무시) · endsTick 은 늘어난 값 */
  | { phase: "countdown"; vs: string; goTick: number; endsTick: number }
  | { phase: "result"; outcome: SumoOutcome; hint: string | null }
  | { phase: "ended" };

/** void = 상대가 20초 안에 돌아오지 않거나 교사가 한쪽을 분리 · 관전으로 빼 무효(승패 기록 없음 · 이유는 알리지 않는다 — SR-SEC-03) */
export type SumoOutcome = "win" | "lose" | "draw" | "void";

export interface CarryDone {
  outcome: "complete" | "incomplete";
  hint: string | null;
}

export interface ModeState {
  /** 밀어내기 — FullState 뒤 이벤트로 바뀐 상태(null = meta 에서) */
  readonly sumo: SumoState | null;
  /** 협동 — 팀 완료 · 끝(null = 아직) */
  readonly carryDone: CarryDone | null;
  /** 리모컨을 보일지 — 이벤트로 바뀐 값(null = meta 에서) */
  readonly driving: boolean | null;
}

export const EMPTY_MODE_STATE: ModeState = { sumo: null, carryDone: null, driving: null };

export function sumoFromMeta(m: RoomMeta["mode"]): SumoState {
  const match = m.match as
    | {
        vs?: string;
        endsTick?: number;
        paused?: { untilTick?: number };
        countdown?: { goTick?: number };
      }
    | undefined;
  if (!match) return { phase: "wait", next: false };
  const vs = match.vs ?? "";
  const endsTick = match.endsTick ?? 0;
  if (match.paused) return { phase: "paused", vs, untilTick: match.paused.untilTick ?? 0 };
  if (match.countdown)
    return { phase: "countdown", vs, goTick: match.countdown.goTick ?? 0, endsTick };
  return { phase: "match", vs, endsTick };
}

/** 내 경기가 멈춤 · 3·2·1 중(리모컨 끔 — 서버도 입력을 쓰지 않는다) */
export const sumoHeld = (s: SumoState): boolean => s.phase === "paused" || s.phase === "countdown";

const vsOf = (s: SumoState): string =>
  s.phase === "match" || s.phase === "paused" || s.phase === "countdown" ? s.vs : "";

/** 밀어내기 이벤트 → 상태(모르는 이벤트는 그대로) */
export function sumoReduce(s: SumoState, e: RoomEvent): SumoState {
  if (e.t === "match")
    return { phase: "match", vs: String(e.vs ?? ""), endsTick: Number(e.endsTick ?? 0) };
  if (e.t === "matchPause")
    return { phase: "paused", vs: vsOf(s), untilTick: Number(e.untilTick ?? 0) };
  if (e.t === "matchResume")
    return {
      phase: "countdown",
      vs: vsOf(s),
      goTick: Number(e.goTick ?? 0),
      endsTick: Number(e.endsTick ?? 0),
    };
  if (e.t === "result")
    return {
      phase: "result",
      outcome: e.outcome as SumoOutcome,
      hint: typeof e.hint === "string" ? e.hint : null,
    };
  if (e.t === "queue") return s.phase === "wait" ? { phase: "wait", next: !!e.next } : s;
  if (e.t === "matchEnd") return s.phase === "wait" ? { phase: "ended" } : s;
  return s;
}

/** 리모컨을 보일지(초기값) — 로비는 늘 · 밀어내기는 내 경기 중 · 협동은 팀이 있을 때 · 릴레이는 따로 */
export function drivingFromMeta(meta: RoomMeta | null): boolean {
  const kind = meta?.mode.kind ?? "lobby";
  return kind === "sumo"
    ? !!meta?.mode.match
    : kind === "carry"
      ? typeof meta?.mode.team === "number"
      : kind !== "relay";
}

const SUMO_EVENTS = new Set(["match", "result", "queue", "matchEnd", "matchPause", "matchResume"]);
const MODE_EVENTS = new Set([...SUMO_EVENTS, "teamComplete", "teamEnd"]);

/** 연결이 받은 이벤트를 접는다 — 모드 이벤트가 아니면 null(바뀐 것 없음) */
export function modeReduce(s: ModeState, e: RoomEvent, meta: RoomMeta | null): ModeState | null {
  if (!MODE_EVENTS.has(e.t)) return null;
  const sumo = SUMO_EVENTS.has(e.t)
    ? sumoReduce(s.sumo ?? (meta ? sumoFromMeta(meta.mode) : { phase: "wait", next: false }), e)
    : s.sumo;
  const carryDone: CarryDone | null =
    e.t === "teamComplete"
      ? { outcome: "complete", hint: null }
      : e.t === "teamEnd"
        ? { outcome: "incomplete", hint: typeof e.hint === "string" ? e.hint : null }
        : s.carryDone;
  const driving =
    e.t === "match"
      ? true
      : e.t === "result" || e.t === "teamComplete" || e.t === "teamEnd"
        ? false
        : s.driving;
  return { sumo, carryDone, driving };
}
